import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Save, Phone, AlertTriangle, PhoneOff, PhoneCall, ClipboardList,
  UserCog,
} from "lucide-react";
import { toast } from "sonner";
import GenesysConnectButton from "@/components/GenesysConnectButton";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { useLeadForm } from "@/hooks/useLeadForm";
import { useGenesysDial, GenesysBadge } from "@/components/leads/DialControls";
import CaptureCard from "@/components/leads/CaptureCard";
import PocHqCards from "@/components/leads/PocHqCards";
import LeadHistoryPanel from "@/components/leads/LeadHistoryPanel";
import ReassignDialog from "@/components/leads/ReassignDialog";
import RetiredNumbers, { RefreshedPill } from "@/components/leads/RetiredNumbers";
import CampaignLeadDetail from "@/pages/CampaignLeadDetail";

export default function LeadDetail() {
  const { active } = useClient();
  // Ticketing-kind tenants running the outbound campaign get the campaign agent screen.
  if (active && active.kind !== "leads") return <CampaignLeadDetail />;
  return <StandardLeadDetail />;
}

function StandardLeadDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const { activeSlug } = useClient();
  const isSoho = activeSlug === "ooredoo-soho";
  const [agentConnected, setAgentConnected] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const fromGenesys = searchParams.get("src") === "genesys";
  const conversationId = searchParams.get("conversation");

  const lf = useLeadForm(id, { followUpExempt: isSoho });
  const {
    lead, setLead, related, loading, saving, save, isDisabledSave, toggleWrongNumber, load,
  } = lf;
  const { genesys, dialing, dial } = useGenesysDial(id, { agentConnected, setLead });

  // Screen-pop hint — surfaces a one-shot toast when Genesys routed us here.
  useEffect(() => {
    if (fromGenesys && lead) {
      toast.info(`Screen-pop from Genesys${conversationId ? ` · conv ${conversationId.slice(0, 8)}` : ""}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromGenesys, lead?.id, conversationId]);

  if (loading) {
    return <div className="text-slate-400 p-6 text-sm">Loading lead…</div>;
  }
  if (!lead) {
    return (
      <div className="p-6">
        <div className="text-sm text-rose-600">Lead not found.</div>
        <Button variant="link" onClick={() => navigate("/leads")}>Back to Leads</Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4" data-testid="lead-detail">
      {/* Header */}
      <div className="col-span-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/leads")} data-testid="lead-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Lead · Read-only from upload</div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-lg font-semibold text-slate-900">{lead.customer_name}</h1>
              <Badge variant="outline" className="font-mono text-[10px]">{lead.customer_id}</Badge>
              <Badge variant="outline" className="font-mono text-[10px]">Class {lead.classification}</Badge>
              {lead.pending_language_assignment && (
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300"
                       data-testid="arabic-pending-badge">
                  Arabic — pending assignment
                </Badge>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500">
              <span>Assignee:</span>
              <span className="font-medium text-slate-700" data-testid="lead-assignee">
                {lead.assigned_to || "unassigned"}
              </span>
              {hasPerm("leads.reassign") && (
                <button
                  type="button"
                  onClick={() => setReassignOpen(true)}
                  className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                  data-testid="lead-reassign-btn"
                >
                  <UserCog className="w-3 h-3" /> Reassign
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <GenesysBadge status={genesys} />
          <GenesysConnectButton
            configured={!!genesys.configured}
            onChange={setAgentConnected}
          />
          {!isSoho && (
            <Button
              size="sm"
              onClick={save}
              disabled={saving || isDisabledSave}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="lead-save"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          )}
        </div>
      </div>

      {/* ============= LEFT column ============= */}
      <div className="space-y-4">
        {/* Uploaded (read-only) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-slate-500" /> Uploaded Data (read-only)
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-xs">
            <Field label="Customer Name" value={lead.customer_name} mono />
            <Field label="Contact Name" value={lead.contact_name || "—"} />
            <div className="col-span-2">
              {(lead.retired_numbers || []).length > 0 ? (
                <>
                  <RetiredNumbers numbers={lead.retired_numbers} box />
                  <Label className="mt-3 block text-[10px] uppercase tracking-wider text-indigo-700" data-testid="new-customer-number-box-title">
                    New Customer Number
                    <span className="ml-1 font-mono normal-case text-slate-400">· client refresh {lead.last_refresh_at ? new Date(lead.last_refresh_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : ""}</span>
                  </Label>
                </>
              ) : (
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">Contact Numbers</Label>
              )}
              <div className="mt-1 flex flex-col gap-1" data-testid="active-numbers-box">
                {(lead.numbers || []).map((n) => (
                  <div
                    key={n.e164}
                    className={`flex items-center justify-between px-2 py-1.5 rounded border ${
                      n.wrong_number ? "border-rose-200 bg-rose-50" : "border-slate-200"
                    }`}
                    data-testid={`num-row-${n.e164}`}
                  >
                    <div className="flex items-center gap-2">
                      <Phone className={`w-3 h-3 ${n.wrong_number ? "text-rose-500" : "text-slate-400"}`} />
                      <span className={`font-mono text-[11px] ${n.wrong_number ? "line-through text-slate-500" : "text-slate-800"}`}>
                        {n.e164}
                      </span>
                      <span className="text-[9px] text-slate-400">{n.slot}</span>
                      <RefreshedPill number={n} />
                    </div>
                    <div className="flex items-center gap-1">
                      {!n.wrong_number && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[10px] text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                          disabled={isSoho || dialing || !genesys.configured}
                          onClick={() => dial(n.e164)}
                          title={
                            isSoho
                              ? "Not available for Ooredoo SOHO"
                              : !genesys.configured
                              ? `Genesys not configured: ${genesys.reason || ""}`
                              : agentConnected
                                ? "Manual dial via your Genesys session"
                                : "Push to Genesys campaign (connect Genesys for direct dial)"
                          }
                          data-testid={`dial-${n.e164}`}
                        >
                          <PhoneCall className="w-3 h-3 mr-1" />
                          {dialing ? "Dialing…" : "Dial"}
                        </Button>
                      )}
                      <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-6 px-2 text-[10px] ${n.wrong_number ? "text-slate-600" : "text-rose-600"}`}
                          disabled={isSoho}
                          title={isSoho ? "Not available for Ooredoo SOHO" : undefined}
                          data-testid={`wn-toggle-${n.e164}`}
                        >
                          <PhoneOff className="w-3 h-3 mr-1" />
                          {n.wrong_number ? "Restore" : "Wrong Number"}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {n.wrong_number ? "Restore this number to rotation?" : "Flag this number as Wrong Number?"}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {n.wrong_number
                              ? "The number will re-enter the dialing rotation and appear in Related Leads warnings again."
                              : `This will exclude ${n.e164} from dialing and surface a warning on all related leads sharing this number.`}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => toggleWrongNumber(n.e164, n.wrong_number)}
                            className={n.wrong_number ? "" : "bg-rose-600 hover:bg-rose-700"}
                          >
                            {n.wrong_number ? "Restore" : "Flag as Wrong"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>


        <CaptureCard {...lf} soho={isSoho} />
        <PocHqCards {...lf} />

        {isSoho && (
          <div className="flex justify-end pt-1 pb-6">
            <Button
              size="sm"
              onClick={save}
              disabled={saving || isDisabledSave}
              className="bg-indigo-600 hover:bg-indigo-700 px-8"
              data-testid="lead-save-bottom"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        )}
      </div>

      {/* ============= RIGHT column — Related + History ============= */}
      <LeadHistoryPanel lead={lead} related={related} />

      <ReassignDialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        leadIds={[lead.id]}
        arabicHint={(lead.call_language || "") === "arabic_only" || !!lead.pending_language_assignment}
        onDone={load}
      />
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-400">{label}</Label>
      <div className={`mt-1 text-slate-800 ${mono ? "font-mono text-[11px]" : ""}`}>{value}</div>
    </div>
  );
}
